import Vue from 'vue';
import Vuex from 'vuex';
import { nanoid } from 'nanoid';
import router from '@/router';
import jwt from 'jsonwebtoken';
import { PublicClientApplication } from '@azure/msal-browser';
import bcrypt from 'bcryptjs';
import io from "socket.io-client";

Vue.use(Vuex);

const config = CONFIG;
const apiDir = config.apiDir;
const portalApiDir = config.portalApiDir;


const protocol = window.location.protocol;
const hostname = window.location.hostname;
const port = config.dev ? config.devApiPort : window.location.port;
const portalBaseUrl = `${protocol}//${hostname}:${port}/`;

const msalConfig = config.msalConfig;
msalConfig.auth.redirectUri = config.serverAddress;
msalConfig.auth.postLogoutRedirectUri = config.serverAddress;
const loginRequest = {
    scopes: config.loginScopes
};

let msalInstance;
let key = nanoid();
let roles = [];

const sign = (data, api = false) => {
    const apiKey = store.state.apiKey;
    return jwt.sign(data, api ? apiKey : key);
};

const verify = (data, api = false) => {
    const apiKey = store.state.apiKey;
    if (apiKey) {
        return jwt.verify(data, api ? apiKey : key);
    }
    return false;
};

const setRoles = (r) => {
    try {
        let authRoles = verify(r, true);
        roles = authRoles.authList;
        if (store.state.userSiteSettings) {
            roles = [store.state.userSiteSettings.role];
        }
    } catch (err) {
        console.error('invalid auth');
    }
};

const hasRole = (r) => {
    return roles.includes(r);
};


const accessible = (rolesToCheck) => {
    const hasAccess = (role) => {
        return hasRole(role) || hasRole('admin');
    };
    if (!Array.isArray(rolesToCheck)) {
        return hasAccess(rolesToCheck);
    }

    let canAccess = true;
    rolesToCheck.forEach((role) => {
        if (!hasAccess(role)) {
            canAccess = false;
        }
    });
    return canAccess;
};

let account = null;

const getoid = () => {
    if (account) {
        try {
            let act = verify(account);
            if (act) {
                return act.username;
            }
        } catch (err) {
            return null;
        }
    }
    return null;
};

const initAxios = () => {
    const oid = getoid();
    const apiKey = store.state.apiKey;
    if (apiKey) {
        const encryptedKey = bcrypt.hashSync(apiKey, 10);
        if (oid) {
            Vue.axios.defaults.headers.common['DC-User'] = oid;
        }
        Vue.axios.defaults.headers.common['DC-Auth'] = encryptedKey;
    }
};

import mmlModule from './mmls.js';

const store = new Vuex.Store({
    state: {
        refresh: true,
        loading: true,
        loggingIn: false,
        errorMessage: null,
        accessToken: '',
        account: null,
        dcauth: null,
        auth: {},
        customerId: null,
        customerData: null,
        userSiteSettings: null,
        portalBaseUrl,
        dorsettUser: null,
        apiKey: null,
        loginError: null,
        analyticsConfig: [],
        analyticsUnitsOfMeasure: [],
        systemsDataLoss: [],
        modelEligibility: {},
        modelInfo: {},
    },
    getters: {
        config () {
            return config;
        },
        msalConfig () {
            return msalConfig;
        },

        loading (state) {
            return state.loading;
        },

        loggingIn (state) {
            return state.loggingIn;
        },

        loggedIn (state) {
            try {
                return !!verify(state.account);
            } catch (err) {
                return false;
            }
        },

        accessible (state) {
            return (roles) => {
                return accessible(roles);
            };
        },

        /**
         * Returns the API key.
         *
         * @param {object} state
         * @returns {string|null} encrypted API key
         */
        apiKey (state) {
            if (state.apiKey) {
                return bcrypt.hashSync(state.apiKey, 10);
            }
            return null;
        },

        userEmail (state) {
            if(state.fullUser){
                return state.fullUser.email;
            }
            return null;
        },

        systemsDataLoss (state) {
            return state.systemsDataLoss;
        },

        analyticsConfig (state) {
            return state.analyticsConfig;
        },

        analyticsUnitsOfMeasure (state) {
            return state.analyticsUnitsOfMeasure;
        },

        customerId (state) {
            return state.customerId;
        },

        customerData (state) {
            return state.customerData;
        },

        userSiteSettings (state) {
            return state.userSiteSettings;
        },

        error (state) {
            return state.error;
        },

        errorMessage (state) {
            return state.errorMessage;
        },

        taskModelId (state) {
            return state.taskModelId;
        },

        getModelEligibility: (state) => (systemId) => {
            return state.modelEligibility[systemId] || null;
        },

        getModelInfo: (state) => (systemId) => {
            return state.modelInfo[systemId] || null;
        },

        analyticsKey: (state) => () => {
            // obtain the key for the selected customer
            if (state.customerData) {
                const keys = state.customerData.keys.filter(k => k.keyType === 'analytics');
                return keys.length ? keys[0].keyContent : null;
            }
        }

    },
    mutations: {
        setUser (state, token) {
            try {
                if (!accessingCentral) {
                    localStorage.setItem('token', token);
                }
                let user = verify(token, true);
                state.account = sign(user);
                state.fullUser = user;
            } catch (err) {
                console.error('error saving user', err);
            }
        },

        setDorsettUser (state, user) {
            state.dorsettUser = {
                name: user.name,
                email: user.username
            };
        },

        setCustomer (state, customerId) {
            state.customerId = customerId;
            this.dispatch('getCustomerData', customerId);
            this.dispatch('fetchAnalyticsConfig');
        },

        error (state, msg) {
            state.errorMessage = msg;
        },

        setLoading (state, loading) {
            state.loading = loading;
        },
        setLoadingLess (state) {
            state.loadingLess = false;
        },
        setError (state, error) {
            state.error = error;
            state.errorMessage = error || '';
        },
        setLoggingIn (state, loggingIn) {
            state.loggingIn = loggingIn;
        },
        setAccessToken (state, token) {
            state.accessToken = sign(token);
        },

        setAccount (state, {rawAccount, redirect}) {
            store.commit('setDorsettUser', rawAccount);
            account = sign(rawAccount);
            state.account = account;
            state.dcauth = nanoid();
            if (store.getters.loggedIn) {
                const path = redirect || '/';
                router.push({
                    path
                });
            }
        },

        setAuth (state, {authList}) {
            state.dcauth = authList;
        },

        /**
         * Sets the API key.
         *
         * @param {object} state
         * @param {string} apiKey
         */
        setApiKey (state, apiKey) {
            state.apiKey = apiKey;
        },

        /**
         * Sets the error message the user will see in the DCCS login page.
         *
         * @param {object} state
         * @param {string} error
         */
        setLoginError (state, error) {
            state.loginError = error;
        },

        updateAnalyticsConfig (state, payload) {
            if (!state.customerData) {
                // Initialize customerData if it doesn't exist
                Vue.set(state, 'customerData', {});
            }
            if (!state.customerData.analyticsConfig) {
                // Initialize analyticsConfig if it doesn't exist
                Vue.set(state.customerData, 'analyticsConfig', {});
            }
            // Update each property individually to ensure reactivity
            Object.keys(payload).forEach(key => {
                Vue.set(state.customerData.analyticsConfig, key, payload[key]);
            });
        },

        insertAnalyticsConfig (state, payload) {
            if (!state.customerData) {
                // Ensure state.customerData exists
                // Initialize customerData if it doesn't exist
                Vue.set(state, 'customerData', {});
            }
            // Use Vue.set to add a new reactive property
            Object.keys(payload).forEach(key => {
                Vue.set(state.customerData.analyticsConfig, key, payload[key]);
            });
        },

        /**
         * should be called by actions
         * @param {object} state
         * @param {object[]} payload
         */
        setAnalyticsConfig (state, payload) {
            if (!state.analyticsConfig) {
                state.analyticsConfig = [];
            }
            state.analyticsConfig = payload;
        },

        /**
         * should be called by actions
         * @param {object} state
         * @param {object[]} payload
         */
        setSystemsDataLoss (state, payload) {
            if (!state.systemsDataLoss) {
                state.systemsDataLoss = [];
            }
            state.systemsDataLoss = payload;
        },

        /**
         *
         * @param {object} state
         * @param {object[]} payload
         */
        setAnalyticsUnitsOfMeasure (state, payload) {
            if (!state.analyticsUnitsOfMeasure) {
                state.analyticsUnitsOfMeasure = [];
            }
            state.analyticsUnitsOfMeasure = payload;

        },

        taskModelId (state, taskId) {
            state.taskModelId = taskId;
        },

        setModelEligibility (state, { systemId, data }) {
            state.modelEligibility = {
                ...state.modelEligibility,
                [systemId]: data
            };
        },

        setModelInfo (state, { systemId, data }) {
            if (!state.modelInfo) {
                state.modelInfo = {};
            }
            state.modelInfo[systemId] = data;
        }
    },
    actions: {

        updateAnalyticsConfig ({ commit }, newSystems) {
            commit('setAnalyticsConfig', newSystems);
        },

        async getPaMonitor ({ commit, state, dispatch }){
            try {
                commit('setError', null);
                const response = await dispatch('post', {
                    url: 'getPaMonitorData',
                    params: {customerId: state.customerId}
                });
                const {result, error} = response;
                if(error){
                    throw new Error(error);
                }
                return result;
            } catch (error) {
                commit('setError', error);
            }
        },

        // authentication/login
        login ({state, dispatch}, redirectPath) {
            state.refresh = false;
            msalInstance.loginPopup(loginRequest).then(async (loginResponse) => {
                await dispatch('handleResponse', {
                    resp: loginResponse,
                    redirect: redirectPath
                });
            }).catch((error) => {});
        },

        logout ({state, dispatch}) {
            const finish = () => {
                state.account = null;
                if (!accessingCentral) {
                    localStorage.setItem('token', null);
                }
                //router.push('/login');
                router.replace('/login', () => {
                    // onComplete callback
                }, (err) => {
                    // onAbort callback
                    if (err && !err.message.includes('Navigation cancelled')) {
                        // console.log('logout error', err);
                    }
                });
            };
            dispatch('disconnectSocket');
            if (msalInstance) {
                msalInstance.logoutRedirect({
                    onRedirectNavigate: (url) => {
                        // Return false if you would like to stop navigation after local logout
                        // console.log('logout redirecting to login');
                        finish();
                        return false;
                    }
                });
            } else {
                finish();
            }
        },

        async handleResponse ({state, commit, dispatch}, {resp, redirect}) {
            commit('setLoggingIn', false);
            if (resp) {
                const apiKey = await dispatch('validateLogin', resp.accessToken);
                if (apiKey) {
                    commit('setApiKey', apiKey);
                    commit('setAccount', {
                        rawAccount: resp.account,
                        redirect
                    });
                }
            } else {
                const currentAccounts = msalInstance.getAllAccounts();
                if (currentAccounts === null) {
                    return;
                } else if (currentAccounts.length > 1) {
                    // Add choose account code here
                    console.warn("Multiple accounts detected.");
                } else if (currentAccounts.length === 1) {
                    commit('setAccount', {rawAccount: currentAccounts[0]});
                }
            }
        },

        async startup ({state, commit}, needsPerm = true) {
            initAxios();
            if (needsPerm) {
                let authList = await Vue.axios.get(`${apiDir}/getPerm`);
                authList = authList.data;
                setRoles(authList && authList.result);
                commit('setAuth', {authList});
            }
        },

        async sendCommand (context, data) {
            let params = {
                data
            };
            let token = sign(params, true);

            let result = await Vue.axios.post(`${apiDir}/attemptAction`, {token});
            if (result && result.data) {
                return result.data;
            }

            return null;
        },

        async post (context, data) {
            const result = await Vue.axios.post(`${portalApiDir}/${data.url}`, data.params);
            return result.data;
        },

        async get (context, data) {
            const result = await Vue.axios.get(`${portalApiDir}/${data.url}`, { params: data.params });
            return result.data;
        },

        async getCustomerData (context, id=context.state.customerId) {
            if (!id) {
                return;
            }
            const result = await this.dispatch('post', { url: 'getCustomer', params: { id } });
            if (result.customer) {
                const customer = result.customer;
                context.state.customerData = customer;
                context.state.userSiteSettings = customer.customersUsers.find(user => user.email === context.state.fullUser.email).customersCustomerUsers;
                context.state.analyticsConfig = customer.systems;

            }
        },
        /**
         * Initializes the socket connection. Requires the API key to be set.
         */
        connectSocket ({getters}) {
            const apiKey = getters.apiKey;
            if (apiKey) {
                window.socket = io.connect(
                    `${window.location.hostname}:${CONFIG.socketPort}`,
                    {
                        secure: !!CONFIG.public,
                        extraHeaders: {
                            'DC-Auth': apiKey
                        }
                    }
                );
            }
        },

        /**
         * Ends the socket connection.
         */
        disconnectSocket () {
            if (window.socket && window.socket.connected) {
                window.socket.disconnect();
                delete window.socket;
            }
        },

        /**
         * Checks that the access token is valid. If the token is valid, the serve will return the API key.
         *
         * @param {object} context Vuex context
         * @param {string} accessToken
         * @returns {string|undefined}
         */
        async validateLogin (context, accessToken) {
            const result = await Vue.axios.post(`/dccsapi/auth/validateLogin`, {accessToken});
            if (result?.data?.error) {
                context.commit('setLoginError', 'There was an error logging you in. Please refresh the page try again. If the problem persists, please contact engineering@dorsettcontrols.com');
            } else {
                return result.data.apiKey;
            }
        },

        /**
         * Sends the config to the server
         * @param {object} param0
         * @param {function} param0.commit
         * @param {object} param0.state
         * @param {object[]} newSystems
         */
        async saveAnalyticsConfig ({ commit, state, dispatch }, newSystems) {
            try {
                commit('setError', null);
                commit('setLoadingLess', true);
                commit('setAnalyticsConfig', newSystems);

                const userEmail = state.fullUser?.email ?? 'not specified';

                const response = await dispatch('post', {
                    url: 'analyticsConfig',
                    params: {
                        config: newSystems,
                        customerId: state.customerId,
                        userEmail
                    }
                });


                const { config = [], error } = response || {};
                if (config?.length) {
                    commit('setAnalyticsConfig', config);
                    return;
                }
                if (error) {
                    throw new Error(error);
                }
            } catch (err) {
                commit('setError', err.message || 'Unknown error occurred');
            } finally {
                commit('setLoadingLess', false);
            }
        },

        async checkForSystemsDataLoss ({ commit, state, dispatch }, newSystems) {
            try {
                commit('setLoadingLess', true);
                commit('setError', null);
                commit('setSystemsDataLoss', []);

                const response = await dispatch('post', {
                    url: 'checkAnalyticsConfig',
                    params: {
                        config: newSystems,
                        customerId: state.customerId,
                    }
                });

                const { systems = [], error } = response || {};
                if (error) {
                    commit('setError', error);
                } else if (Array.isArray(systems)) {
                    commit('setSystemsDataLoss', systems);
                } else {
                    commit('setError', 'Unexpected response format');
                }

            } catch (error) {
                commit('setError', error.message || 'Unknown error occurred');

            } finally {
                commit('setLoadingLess', false);
            }
        },

        /**
         * Obtain customer's configuration from server
         * and sets the local state of analyticsConfig
         * @param {object} param0
         * @param {Function} param0.commit
         */
        async fetchAnalyticsConfig ({ commit }) {
            if(this.getters.customerId){
                try {
                    commit('setError', null);
                    commit('setLoading', true);

                    /** @type {id:string, system_name:string}[] */
                    const config = await this.dispatch('get', { url: 'analyticsConfig', params: { customerId: this.getters.customerId } });
                    commit('setAnalyticsConfig', config);
                } catch (error) {
                    commit('setError', error);
                } finally {
                    commit('setLoading', false);
                }
            }
        },

        /**
         *
         * @param {object} param0
         * @param {Function} param0.commit
         */
        async fetchAnalyticsUnitsOfMeasure ({commit}){
            try {
                commit('setError', null);
                commit('setLoadingLess', true);

                const uom = await this.dispatch('get', {url: 'analyticsUnitsOfMeasure'});
                commit('setAnalyticsUnitsOfMeasure', uom);
            } catch (error) {
                commit('setError', error);
            } finally {
                commit('setLoadingLess', false);
            }
        },

        // createModel
        async createAnalyticsModel ({ commit, state }, {systemId, userEmail}) {
            try {
                commit('setError', null);
                commit('setLoadingLess', true);

                const taskInfo = await this.dispatch('post', {
                    url: 'analyticsCreateModel',
                    params: { systemId, userEmail }
                });
                if (taskInfo && taskInfo.task_id) {
                    commit('taskModelId', taskInfo.task_id);
                }
            } catch (error) {
                commit('setError', error);
            } finally {
                commit('setLoadingLess', false);
            }
        },

        async generateClientFile ({ commit, state, dispatch }, systemId) {
            try {
                commit('setError', null);
                const response = await dispatch('post', {
                    url: 'generateAnalyticsClient',
                    params: {
                        systemId,
                    }
                });

                if (!response) {
                    commit('setError', 'problem');
                }
                return response;

            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },

        async checkModelEligibility ({ dispatch, commit }, systemId) {
            try {
                const response = await Vue.axios.get(`/portal/analyticsModelEligibility`, {
                    params: { systemId }
                });
                // const { error } = response || {};
                commit('setModelEligibility', {systemId, data: response.data});
                return response.data;
            } catch (error) {
                const fallback = {
                    canCreate: false,
                    reason: 'Unable to determine model status at this time.'
                };

                commit('setModelEligibility', { systemId, data: fallback });

                // Commit the error to Vuex
                commit('setError',error?.message || 'Unknown error');
            }
        },

        async fetchModelInfo ({ commit, dispatch }, systemId) {
            try {
                commit('setError', null);
                const response = await Vue.axios.get(`/portal/analyticsModelInfo`, {
                    params: { systemId }
                });
                commit('setModelInfo', {systemId, data: response.data});
                // add a promise timeout here
                return response.data;
            } catch (error) {
                commit('setError', error.message || 'Failed to fetch model info');
            }
        },

        async fetchPoints ({ commit, dispatch }, { systemId }) {
            try {
                commit('setError', null);
                const response = await Vue.axios.get(`/portal/analyticsFetchPoints`, {
                    params: { systemId }
                });
                // add a promise timeout here
                if(response.data?.success){
                    // call another action to refresh the systems
                    await dispatch('fetchAnalyticsConfig');
                    return {success: true};
                }
            } catch (error) {
                commit('setError', error.message || 'Failed to fetch points');
            }
        },

    },

    modules: {
        mmls: {
            namespaced: true,
            ...mmlModule
        }
    }
});

const accessingCentral = window.location.host.match('central') || (window.location.host.match('localhost') && config.defaultDir === 'central');

if (!accessingCentral) {
    const token = localStorage.getItem('token');
    if (token) {
        store.commit('setUser', token);
    }
}

if (accessingCentral) {
    store.state.loggingIn = false;
    msalInstance = new PublicClientApplication(store.getters.msalConfig);
    msalInstance.handleRedirectPromise().then((response) => {
        store.dispatch('handleResponse', {
            resp: response
        });
    }).catch(err => {
        console.error(err);
    });
} else {
    store.state.loggingIn = false;
}

export default store;
